import { useTranslation } from "react-i18next";
import {
  Activity,
  ArrowRight,
  FileText,
  Leaf,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

import { Button } from "@shared/ui/button";

import { localizedHref, resolveLang } from "../lib/links";

interface ProcessStep {
  id: string;
  icon: LucideIcon;
}

const PROCESS_STEPS: ProcessStep[] = [
  { id: "choose", icon: Leaf },
  { id: "track", icon: Activity },
  { id: "learn", icon: Sparkles },
  { id: "share", icon: FileText },
];

export function ProcessSection() {
  const { t, i18n } = useTranslation("landing");
  const lang = resolveLang(i18n.resolvedLanguage);

  return (
    <section
      id="process"
      className="relative overflow-hidden bg-primary py-20 text-primary-foreground sm:py-24 lg:py-28"
    >
      <div
        className="pointer-events-none absolute -top-24 end-[-6rem] -z-10 h-96 w-96 rounded-full bg-primary-foreground/15 blur-3xl"
        aria-hidden="true"
      />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.28em] text-primary-foreground/70">
            {t("process.eyebrow")}
          </p>
          <h2 className="mt-4 font-serif text-4xl font-semibold leading-[1.1] tracking-[-0.04em] sm:text-5xl lg:text-6xl">
            {t("process.heading")}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-primary-foreground/80">
            {t("process.subheading")}
          </p>
        </div>

        <ol className="relative mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {PROCESS_STEPS.map((step, index) => {
            const StepIcon = step.icon;
            const isLast = index === PROCESS_STEPS.length - 1;
            return (
              <li
                key={step.id}
                className="group relative rounded-3xl border border-primary-foreground/20 bg-primary-foreground/10 p-6 backdrop-blur transition-colors duration-300 hover:border-primary-foreground/40 hover:bg-primary-foreground/15"
              >
                {!isLast && (
                  <span
                    className="pointer-events-none absolute end-[-1rem] top-1/2 hidden h-px w-8 -translate-y-1/2 bg-primary-foreground/25 lg:block"
                    aria-hidden="true"
                  />
                )}
                <div className="mb-5 flex items-center justify-between gap-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-foreground text-primary shadow-md">
                    <StepIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <span className="font-serif text-5xl leading-none text-primary-foreground/20">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="text-xl font-semibold">
                  {t(`process.steps.${step.id}.title`)}
                </h3>
                <p className="mt-3 text-sm leading-6 text-primary-foreground/75">
                  {t(`process.steps.${step.id}.description`)}
                </p>
              </li>
            );
          })}
        </ol>

        <div className="mt-12 flex justify-center">
          <Button
            size="lg"
            className="bg-background text-foreground hover:bg-background/90"
            asChild
          >
            <a href={localizedHref("/register", lang)}>
              {t("process.cta")}
              <ArrowRight className="h-4 w-4 rtl:scale-x-[-1]" aria-hidden="true" />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}

export default ProcessSection;
